// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Rendered into <body>, not where it is written. A drawer is written inside the page that owns it,
 * and the page's layout reached it: inside a `space-y-*` container Tailwind gives every child after
 * the first a `margin-top`, and a `position: fixed` element still takes its margin — so the drawer
 * sat 20px below the top of the window with its footer (Save, Publish) cut off the bottom. A
 * parent with a transform or its own stacking context would do worse. Portaling is the rung
 * ui/layers.js names for anything that must escape its parent.
 */
// `widthClass` is the venue module's name for `width` (merged from its kit); either works.
export const Drawer = ({ open, onClose, title, subtitle, status, children, actions, width, widthClass }) => {
  const w = width || widthClass || 'w-[560px]';
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      // An Escape already claimed by something open inside (a list, a menu) is that thing's, not the drawer's.
      if (event.key === 'Escape' && !event.defaultPrevented) onClose?.();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return createPortal((
    <>
      {open && (
        <div className="fixed inset-0 bg-black/20 z-40 transition-opacity" onClick={onClose} />
      )}
      {/* OPEN MEANS NO TRANSFORM AT ALL, not `translate-x-0`. Any transform, even a zero one, makes
          this panel the containing block for every `position: fixed` element inside it, so a dialog
          opened from a drawer (ui/Modal, the OTP prompt) had its backdrop clipped to the drawer and
          sat centred in it instead of on the screen. `transform-none` still animates from
          translate-x-full, because `none` interpolates as the identity. */}
      <div
        role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined} aria-hidden={open ? undefined : 'true'}
        className={`fixed top-0 right-0 h-screen ${w} bg-white border-l border-[#E6E4E1] z-50 transform transition-transform duration-200 shadow-[-8px_0_24px_-12px_rgba(0,0,0,0.08)] flex flex-col ${
          open ? 'transform-none' : 'translate-x-full'
        }`}
      >
        <div className="px-6 pt-5 pb-4 border-b border-[#E6E4E1] flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] tracking-[0.08em] font-semibold uppercase text-[#9CA3AF] mb-1">{subtitle}</div>
              <div className="text-[17px] font-semibold tracking-[-0.01em] text-[#1F2937] truncate">{title}</div>
              {status && <div className="mt-2">{status}</div>}
            </div>
            <button type="button" aria-label="Close" onClick={onClose} className="w-8 h-8 rounded-md hover:bg-[#F2F1ED] flex items-center justify-center flex-shrink-0">
              <X className="w-4 h-4 text-[#6B7280]" strokeWidth={1.75} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
        {actions && (
          <div className="border-t border-[#E6E4E1] px-5 py-2.5 flex items-center justify-end gap-1.5 gap-y-2 flex-wrap bg-[#FBFAF7] flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </>
  ), document.body);
};


export const DrawerSection = ({ title, children, action }) => (
  <div className="px-6 py-4 border-b border-[#E6E4E1] last:border-b-0">
    <div className="flex items-center justify-between mb-3">
      <div className="text-[10.5px] tracking-[0.08em] font-semibold uppercase text-[#6B7280]">{title}</div>
      {action}
    </div>
    {children}
  </div>
);


export const DrawerActions = ({ children }) => (
  <div className="px-5 py-3 border-t border-[#E6E4E1] bg-[#FBFAF7] flex items-center justify-between gap-2 sticky bottom-0">
    {children}
  </div>
);
